import styles from './FocusModeTimer.module.scss';

const WARNING_THRESHOLD_SECONDS = 600; // 10 minutes

interface FocusModeTimerProps {
  remainingSeconds: number;
  effectiveTimeSeconds: number;
  isExpired: boolean;
  // Optional prop to adjust styling when rendered in the header and for initial tests
  isInHeader?: boolean;
}

type TimerStatus = 'running' | 'warning' | 'finished';

function getTimerStatus(
  isExpired: boolean,
  remainingSeconds: number,
): TimerStatus {
  if (isExpired) return 'finished';
  if (remainingSeconds <= WARNING_THRESHOLD_SECONDS) return 'warning';
  return 'running';
}

/**
 * Formats seconds into minutes only (no seconds display).
 * For example: 45, 120, 65
 */
export function formatTime(seconds: number): number {
  const safeSeconds = Math.max(0, seconds);
  return Math.floor(safeSeconds / 60);
}

/**
 * FocusModeTimer
 *
 * Visual countdown timer for Focus Mode. adds MM:SS / H:MM:SS formatting and a warning state
 * (amber) when remaining time is <= 10 minutes.
 *
 * Rendered by FocusModeProvider as a fixed overlay, persisting across steps.
 */
function FocusModeTimer({
  remainingSeconds,
  effectiveTimeSeconds,
  isExpired,
  isInHeader = false,
}: FocusModeTimerProps): JSX.Element | null {
  if (effectiveTimeSeconds <= 0) return null;

  const status = getTimerStatus(isExpired, remainingSeconds);

  const safeRemaining = Math.max(0, remainingSeconds);
  const safeTotal = Math.max(1, effectiveTimeSeconds);
  const elapsed = safeTotal - safeRemaining;
  const progressPercentage = (elapsed / safeTotal) * 100;

  const circumference = 2 * Math.PI * 45;
  const dashoffset = circumference - (progressPercentage / 100) * circumference;

  const remainingMinutes = formatTime(safeRemaining);
  const timeLabel = `${remainingMinutes} min`;

  return (
    <div className={`${styles.wrapper} ${isInHeader ? styles.inHeader : ''}`}>
      <div
        role="timer"
        aria-live="polite"
        aria-label={`Tempo restante: ${timeLabel}`}
        data-testid="focus-mode-timer"
        className={styles.timerContainer}
      >
        <div className={styles.progressBarWrapper}>
          <svg className={styles.progressBarSvg} viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r="45"
              className={styles.progressBarTrack}
              aria-hidden="true"
            />
            <circle
              cx="50"
              cy="50"
              r="45"
              data-testid="focus-mode-timer-progress"
              className={`${styles.progressBarProgress} ${styles[status]}`}
              style={{strokeDashoffset: dashoffset}}
              aria-hidden="true"
            />
          </svg>
          <div className={styles.timerText} aria-hidden="true">
            {timeLabel}
            <br />
            <span className={styles.timerTextSub}>restantes</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FocusModeTimer;
